import { countriesData } from './const'

// Address suggestions over Places API (New) REST. No Google Maps <script> is ever loaded: that keeps
// third-party JS off the screens that hold patient details (plan decision 2026-10-08). The browser key
// is referrer-restricted to the care hosts and restricted to places.googleapis.com.

const PLACES_BASE = 'https://places.googleapis.com/v1'
const MIN_CHARS = 3
const MAX_SUGGESTIONS = 5

export function hasPlacesKey() {
  return !!process.env.REACT_APP_GOOGLE_PLACES_KEY
}

// One token per address-entry session: every autocomplete call and the final details call share it,
// so Google bills them as one session.
export function newSessionToken() {
  const c = typeof crypto !== 'undefined' ? crypto : undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16)
    return (ch === 'x' ? r : (r % 4) + 8).toString(16)
  })
}

export async function suggest(
  input,
  { key, sessionToken, fetchImpl, signal } = {},
) {
  const text = (input || '').trim()
  if (!key || text.length < MIN_CHARS) return []
  try {
    const doFetch = fetchImpl || fetch
    const res = await doFetch(`${PLACES_BASE}/places:autocomplete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': key },
      body: JSON.stringify({
        input,
        includedRegionCodes: ['ca', 'us'],
        sessionToken,
      }),
      signal,
    })
    if (!res || !res.ok) return []
    const body = await res.json()
    return (body?.suggestions || [])
      .map((s) => s.placePrediction)
      .filter((p) => p && p.placeId)
      .slice(0, MAX_SUGGESTIONS)
      .map((p) => ({
        placeId: p.placeId,
        line: p.structuredFormat?.mainText?.text || p.text?.text || '',
        secondary: p.structuredFormat?.secondaryText?.text || '',
      }))
  } catch (e) {
    return []
  }
}

// Google's administrative_area_level_1 shortText -> the province/state names in countriesData.
const CA_CODES = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NT: 'Northwest Territories',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon Territory',
}
const US_CODES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}
const COUNTRY_CODES = { CA: 'Canada', US: 'United States' }
const STATE_CODES = { Canada: CA_CODES, 'United States': US_CODES }

// Only returns a name the country's state <select> actually offers; anything else -> ''.
export function stateNameFor(country, code) {
  const name = STATE_CODES[country]?.[code]
  if (!name) return ''
  const states =
    countriesData.countries.find((c) => c.country === country)?.states || []
  return states.includes(name) ? name : ''
}

export async function resolve(
  placeId,
  { key, sessionToken, fetchImpl, signal } = {},
) {
  if (!key || !placeId) return null
  try {
    const query = sessionToken
      ? `?sessionToken=${encodeURIComponent(sessionToken)}`
      : ''
    const doFetch = fetchImpl || fetch
    const res = await doFetch(
      `${PLACES_BASE}/places/${encodeURIComponent(placeId)}${query}`,
      {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': key,
          'X-Goog-FieldMask': 'addressComponents,formattedAddress',
        },
        signal,
      },
    )
    if (!res || !res.ok) return null
    const body = await res.json()
    const parts = body?.addressComponents || []
    const find = (type) => parts.find((p) => (p.types || []).includes(type))
    const long = (type) => find(type)?.longText || ''
    const country = COUNTRY_CODES[find('country')?.shortText] || ''
    return {
      line: [long('street_number'), long('route')].filter(Boolean).join(' '),
      city: long('locality') || long('sublocality') || long('postal_town'),
      state: stateNameFor(
        country,
        find('administrative_area_level_1')?.shortText,
      ),
      country,
      postal: long('postal_code'),
    }
  } catch (e) {
    return null
  }
}
